import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/public/SiteFooter";
import PhotoGallery from "@/components/PhotoGallery";
import ReviewsSection from "@/components/ReviewsSection";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Minus, Plus, MapPin, Clock, ShoppingBag, CreditCard, CheckCircle2, ChevronLeft, Star } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const SLOTS = ["08:00 - 10:00", "10:00 - 12:00", "12:00 - 14:00", "14:00 - 16:00", "16:00 - 18:00"];
const STEPS = ["Select items", "Pickup details", "Review & pay"];

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const slotStart = (date, slot) => (date ? new Date(`${date}T${slot.split("-")[0].trim()}:00`) : null);

export default function LaundryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [laundry, setLaundry] = useState(null);
  const [qty, setQty] = useState({});
  const [step, setStep] = useState(0);
  const [pickup, setPickup] = useState({ date: "", slot: SLOTS[0], delivery_date: "", delivery_slot: SLOTS[0], address: "", notes: "", needs_delivery: true, use_credit: false });
  const [submitting, setSubmitting] = useState(false);
  const [coupon, setCoupon] = useState({ code: "", applied: null });

  useEffect(() => {
    api.get(`/laundries/${id}`).then((res) => setLaundry(res.data)).catch(() => toast.error("Laundry not found"));
  }, [id]);

  const items = laundry?.items || [];
  const grouped = useMemo(() => {
    const g = {};
    items.forEach((it) => {
      (g[it.category] = g[it.category] || []).push(it);
    });
    return g;
  }, [items]);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + (qty[it.id] || 0) * it.price, 0),
    [items, qty]
  );
  const deliveryFee = pickup.needs_delivery ? 4.99 : 0;
  const discount = coupon.applied
    ? (coupon.applied.discount_type === "percent" ? Math.round(subtotal * coupon.applied.value) / 100 : Math.min(coupon.applied.value, subtotal))
    : 0;
  const total = Math.max(0, subtotal - discount + deliveryFee);
  const itemCount = Object.values(qty).reduce((a, b) => a + b, 0);

  const minH = laundry?.min_turnaround_hours ?? 24;
  const pickupDt = slotStart(pickup.date, pickup.slot);
  const earliest = pickupDt ? new Date(pickupDt.getTime() + minH * 3600000) : null;
  const deliveryDt = slotStart(pickup.delivery_date, pickup.delivery_slot);
  const deliveryOk = !pickup.needs_delivery || (!!earliest && !!deliveryDt && deliveryDt >= earliest);
  const deliveryError = pickup.needs_delivery && pickup.date && pickup.delivery_date && !deliveryOk
    ? `Delivery must be at least ${minH} hours after pickup — earliest is ${earliest.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}.`
    : "";
  const step1Ok = !!pickup.date && !!pickup.address && deliveryOk;

  const applyCoupon = async () => {
    if (!coupon.code) return;
    try {
      const { data } = await api.get("/coupons/validate", { params: { code: coupon.code, laundry_id: id } });
      setCoupon({ ...coupon, applied: data });
      toast.success(`Coupon ${data.code} applied`);
    } catch (e) { setCoupon({ ...coupon, applied: null }); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setItemQty = (itemId, delta) =>
    setQty((q) => ({ ...q, [itemId]: Math.max(0, (q[itemId] || 0) + delta) }));

  const proceedToPay = async () => {
    if (!user) {
      toast.message("Please log in as a customer to book");
      return navigate("/login");
    }
    if (user.role !== "customer") return toast.error("Only customers can book. Log in with a customer account.");
    if (!pickup.date || !pickup.address) return toast.error("Add a pickup date and address");
    if (!deliveryOk) return toast.error(`Choose a delivery slot at least ${minH} hours after pickup`);

    setSubmitting(true);
    try {
      const orderItems = items.filter((it) => qty[it.id] > 0).map((it) => ({ item_id: it.id, qty: qty[it.id] }));
      const { data: order } = await api.post("/orders", {
        laundry_id: id,
        items: orderItems,
        pickup_date: pickup.date,
        pickup_slot: pickup.slot,
        delivery_date: pickup.delivery_date,
        delivery_slot: pickup.delivery_slot,
        address: pickup.address,
        notes: pickup.notes,
        needs_delivery: pickup.needs_delivery,
        use_credit: pickup.use_credit,
        coupon_code: coupon.applied ? coupon.applied.code : "",
      });
      const { data } = await api.post("/payments/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
      setSubmitting(false);
    }
  };

  if (!laundry)
    return (
      <div className="public-site min-h-screen bg-slate-50">
        <Navbar />
        <div className="mx-auto max-w-5xl px-5 py-20 text-center text-slate-400">Loading…</div>
      </div>
    );

  return (
    <div className="public-site min-h-screen bg-slate-50">
      <Navbar dark />
      <section className="hero-glow relative -mt-[72px] overflow-hidden pt-[72px] text-white" data-testid="laundry-hero">
        {laundry.cover_image && (
          <>
            <img src={laundry.cover_image} alt={laundry.name} className="absolute inset-0 h-full w-full object-cover opacity-40" data-testid="laundry-hero-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
          </>
        )}
        <div className="relative mx-auto max-w-6xl px-5 pb-12 pt-8 lg:px-8">
          <button onClick={() => navigate("/search")} className="mb-6 flex items-center gap-1 text-sm font-semibold text-white/60 transition-colors hover:text-white" data-testid="back-to-search">
            <ChevronLeft className="h-4 w-4" /> Back to search
          </button>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-orange">{laundry.city}</p>
          <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">{laundry.name}</h1>
          {laundry.rating?.count > 0 && (
            <div className="mt-3 flex items-center gap-1.5 text-sm" data-testid="laundry-rating">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span className="font-semibold text-white">{laundry.rating.avg}</span>
              <span className="text-white/50">({laundry.rating.count} reviews)</span>
            </div>
          )}
          <p className="mt-4 max-w-2xl text-white/70">{laundry.description}</p>
          <div className="mt-5 inline-flex flex-wrap gap-4 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/80">
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-brand-orange" /> {laundry.address}, {laundry.city} {laundry.postcode}</span>
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-6xl px-5 py-8 lg:px-8">

        {laundry.gallery?.length > 0 && <div className="mb-8"><PhotoGallery photos={laundry.gallery} name={laundry.name} /></div>}
        {/* stepper */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 items-center gap-2">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${i <= step ? "bg-brand text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`hidden text-sm sm:block ${i <= step ? "font-medium text-slate-900" : "text-slate-400"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-brand" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* Step 0 — items */}
            {step === 0 && (
              <div className="space-y-6">
                {items.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
                    This laundry hasn't added a price list yet.
                  </div>
                )}
                {Object.entries(grouped).map(([cat, list]) => (
                  <div key={cat} className="rounded-xl border border-slate-200 bg-white p-6">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{cat}</h3>
                    <div className="mt-4 divide-y divide-slate-100">
                      {list.map((it) => (
                        <div key={it.id} className="flex items-center justify-between py-3" data-testid={`item-row-${it.id}`}>
                          <div>
                            <p className="font-medium text-slate-900">{it.name}</p>
                            <p className="font-mono text-sm text-slate-500">£{it.price.toFixed(2)}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setItemQty(it.id, -1)} data-testid={`decr-${it.id}`} disabled={!qty[it.id]}>
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-6 text-center font-mono font-semibold" data-testid={`qty-${it.id}`}>{qty[it.id] || 0}</span>
                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setItemQty(it.id, 1)} data-testid={`incr-${it.id}`}>
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 1 — pickup */}
            {step === 1 && (
              <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6">
                <div className="space-y-1.5">
                  <Label htmlFor="date">Pickup date</Label>
                  <Input id="date" type="date" value={pickup.date} min={new Date().toISOString().split("T")[0]} onChange={(e) => setPickup({ ...pickup, date: e.target.value })} data-testid="pickup-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Time slot</Label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {SLOTS.map((s) => (
                      <button key={s} onClick={() => setPickup({ ...pickup, slot: s })} data-testid={`slot-${s}`}
                        className={`rounded-lg border px-3 py-2 text-sm transition-all ${pickup.slot === s ? "border-brand bg-accent text-brand" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="address">Pickup address</Label>
                  <Input id="address" value={pickup.address} onChange={(e) => setPickup({ ...pickup, address: e.target.value })} placeholder="221B Baker Street, London" data-testid="pickup-address" />
                </div>
                {pickup.needs_delivery && (
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Delivery back</p>
                    <p className="mb-3 text-xs text-slate-500" data-testid="delivery-turnaround-note">This laundry needs at least {minH} hours between pickup and delivery.</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input type="date" value={pickup.delivery_date} min={earliest ? ymd(earliest) : pickup.date || ymd(new Date())} onChange={(e) => setPickup({ ...pickup, delivery_date: e.target.value })} data-testid="delivery-date" />
                      <select value={pickup.delivery_slot} onChange={(e) => setPickup({ ...pickup, delivery_slot: e.target.value })} className="rounded-md border border-input bg-background px-3 text-sm" data-testid="delivery-slot">
                        {SLOTS.map((s) => {
                          const dt = slotStart(pickup.delivery_date, s);
                          const bad = !!(earliest && dt && dt < earliest);
                          return <option key={s} value={s} disabled={bad}>{s}{bad ? " (too early)" : ""}</option>;
                        })}
                      </select>
                    </div>
                    {deliveryError && <p className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="delivery-error">{deliveryError}</p>}
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea id="notes" value={pickup.notes} onChange={(e) => setPickup({ ...pickup, notes: e.target.value })} placeholder="Ring the doorbell, leave with concierge…" data-testid="pickup-notes" />
                </div>
                <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm" data-testid="delivery-toggle">
                  <input type="checkbox" checked={pickup.needs_delivery} onChange={(e) => setPickup({ ...pickup, needs_delivery: e.target.checked })} className="h-4 w-4 accent-[#2563EB]" />
                  Pickup & delivery by a local driver (+£4.99)
                </label>
              </div>
            )}

            {/* Step 2 — review */}
            {step === 2 && (
              <div className="rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="font-heading text-lg font-medium text-slate-900">Review your order</h3>
                <div className="mt-4 divide-y divide-slate-100">
                  {items.filter((it) => qty[it.id] > 0).map((it) => (
                    <div key={it.id} className="flex justify-between py-2 text-sm">
                      <span className="text-slate-600">{qty[it.id]} × {it.name}</span>
                      <span className="font-mono text-slate-900">£{(qty[it.id] * it.price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 space-y-1.5 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                  <p className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Collect: {pickup.date || "—"} · {pickup.slot}</p>
                  {pickup.needs_delivery && <p className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Deliver: {pickup.delivery_date || "—"} · {pickup.delivery_slot}</p>}
                  <p className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {pickup.address || "—"}</p>
                </div>
                {user?.credit > 0 && (
                  <label className="mt-4 flex items-center justify-between rounded-lg border border-brand/30 bg-accent/40 p-3 text-sm" data-testid="use-credit-toggle">
                    <span className="font-medium text-slate-800">Apply my £{user.credit.toFixed(2)} referral credit</span>
                    <input type="checkbox" checked={pickup.use_credit} onChange={(e) => setPickup({ ...pickup, use_credit: e.target.checked })} className="h-4 w-4 accent-[#2563EB]" />
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-medium text-slate-900">
                <ShoppingBag className="h-5 w-5 text-brand" /> Your basket
              </h3>
              <p className="mt-1 text-sm text-slate-500">{itemCount} item{itemCount !== 1 ? "s" : ""}</p>
              <div className="mt-4 space-y-2 border-t border-slate-100 pt-4 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">£{subtotal.toFixed(2)}</span></div>
                {discount > 0 && <div className="flex justify-between text-green-600"><span>Coupon {coupon.applied.code}</span><span className="font-mono">−£{discount.toFixed(2)}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Delivery</span><span className="font-mono">£{deliveryFee.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-slate-100 pt-2 font-heading text-lg font-semibold text-slate-900"><span>Total</span><span className="font-mono" data-testid="basket-total">£{total.toFixed(2)}</span></div>
              </div>
              <div className="mt-4 flex gap-2">
                <Input value={coupon.code} onChange={(e) => setCoupon({ ...coupon, code: e.target.value })} placeholder="Coupon code" className="h-9 uppercase" data-testid="coupon-input" />
                <Button variant="secondary" className="h-9" onClick={applyCoupon} data-testid="apply-coupon-btn">Apply</Button>
              </div>

              <div className="mt-6 space-y-2">
                {step > 0 && (
                  <Button variant="secondary" className="w-full" onClick={() => setStep(step - 1)} data-testid="step-back">Back</Button>
                )}
                {step < 2 && (
                  <Button className="w-full bg-brand hover:bg-brand-hover" disabled={(step === 0 && itemCount === 0) || (step === 1 && !step1Ok)} onClick={() => setStep(step + 1)} data-testid="step-next">
                    Continue
                  </Button>
                )}
                {step === 2 && (
                  <Button className="w-full bg-brand hover:bg-brand-hover" disabled={submitting} onClick={proceedToPay} data-testid="pay-now-btn">
                    <CreditCard className="mr-2 h-4 w-4" /> {submitting ? "Redirecting…" : `Pay £${total.toFixed(2)}`}
                  </Button>
                )}
              </div>
              {!user && step === 2 && <p className="mt-3 text-center text-xs text-slate-400">You'll be asked to log in to pay.</p>}
            </div>
          </div>
        </div>
        <ReviewsSection rating={laundry.rating} reviews={laundry.reviews} />
      </div>
      <SiteFooter />
    </div>
  );
}
